import { useCallback, useState } from 'react';

/**
 * Der gemerkte Zustand der Menüspalte — eingeklappt und welche Kategorien offen
 * sind.
 *
 * **Der Anfangswert ist die wichtigste Zeile dieser Datei: alle Kategorien
 * offen.** Ein Klappzustand ist ein ZWEITER Weg, auf dem ein Menüpunkt
 * unsichtbar sein kann, und er sieht aus wie der erste (die Fähigkeit fehlt).
 * Ein zugeklapptes «Buchhaltung» verbirgt «Journal» — den Punkt, an dem die
 * Erstanmeldung gemessen wird —, und ein aus einer früheren Sitzung gemerkter
 * Zustand würfe dann einen Prüflauf um, der mit Menüs nichts zu tun hat.
 *
 * `playwright.config.ts` setzt **keinen** `storageState`; jeder Prüffall
 * startet mit leerem Speicher. Mit «alle offen» als Anfangswert sieht jeder
 * Lauf jeden Punkt, genau wie vor der Zweistufigkeit — und keine der neun
 * Menügriffe in fünf Prüfdateien braucht einen vorangestellten Klick.
 *
 * **Jeder Zugriff steht in `try`/`catch`.** In einem privaten Fenster mit
 * abgeschaltetem Speicher wirft `localStorage` schon beim LESEN; ein Fehler an
 * dieser Stelle verhinderte das Laden des Bündels. Dieselbe Falle wie im
 * Inline-Skript von `index.html`.
 *
 * **Die Schlüssel tragen den Vorsatz `fameacc.`** wie der des Farbschemas: Der
 * lokale Speicher gehört der Herkunft und nicht dieser Anwendung allein.
 */

export const EINGEKLAPPT_SCHLUESSEL = 'fameacc.seitenspalte.eingeklappt';
export const OFFEN_SCHLUESSEL = 'fameacc.seitenspalte.offen';

/**
 * Der gemerkte Einklappzustand. Alles ausser dem Wort `ja` heisst ausgeklappt.
 *
 * Ein verstümmelter Eintrag ist damit kein Fehlerfall, sondern die
 * Voreinstellung — dieselbe Regel wie beim Farbschema.
 */
export function gemerktEingeklappt(): boolean {
  try {
    return window.localStorage.getItem(EINGEKLAPPT_SCHLUESSEL) === 'ja';
  } catch {
    return false;
  }
}

/**
 * Die gemerkten offenen Kategorien, gemessen an der heutigen Gliederung.
 *
 * **Drei Fälle, und der mittlere ist der, den man falsch macht:**
 *
 * | Gespeichert | Ergebnis |
 * |---|---|
 * | nichts (`null`) | **alle** Kategorien offen — der Anfangswert |
 * | eine gültige Liste, auch die leere | genau diese Liste |
 * | etwas Unlesbares | **alle** Kategorien offen |
 *
 * Eine gespeicherte leere Liste heisst «der Anwender hat alles zugeklappt» und
 * darf NICHT auf den Anfangswert zurückfallen — sonst liesse sich die letzte
 * Kategorie nie schliessen, und der Umschalter täte beim Neuladen nichts.
 *
 * Unbekannte Schlüssel werden verworfen: Eine Kategorie, die es nicht mehr
 * gibt, verschwindet damit von selbst aus dem Speicher, statt ihn ewig zu
 * belegen.
 */
export function gemerkteOffeneGruppen(alleSchluessel: readonly string[]): readonly string[] {
  let roh: string | null;
  try {
    roh = window.localStorage.getItem(OFFEN_SCHLUESSEL);
  } catch {
    return alleSchluessel;
  }
  if (roh === null) return alleSchluessel;
  try {
    const gelesen: unknown = JSON.parse(roh);
    if (!Array.isArray(gelesen)) return alleSchluessel;
    return alleSchluessel.filter((schluessel) => gelesen.includes(schluessel));
  } catch {
    return alleSchluessel;
  }
}

/** Merken. Ein fehlgeschlagenes Merken ist kein Grund, nicht umzuschalten. */
function merken(schluessel: string, wert: string): void {
  try {
    window.localStorage.setItem(schluessel, wert);
  } catch {
    // Kein Speicher: Die Wahl gilt für diese Sitzung. Das ist besser als ein
    // Umschalter, der nichts tut.
  }
}

/** Der Zustand der Menüspalte samt seiner beiden Umschalter. */
export interface Seitenspalte {
  readonly eingeklappt: boolean;
  readonly einklappenUmschalten: () => void;
  readonly offen: ReadonlySet<string>;
  /**
   * Klappt eine Kategorie auf oder zu. **Bei eingeklappter Spalte fährt sie
   * zuerst auf** und die Kategorie geht auf — nie zu: Ein Klick auf ein
   * Sinnbild in der schmalen Spalte ist der Wunsch, etwas zu sehen.
   */
  readonly gruppeUmschalten: (key: string) => void;
}

/**
 * Liest den gemerkten Zustand und schaltet ihn um.
 *
 * **`aktiveKategorie` wirkt nur beim ersten Anstrich, und das ist Absicht.**
 * Beim Direktaufruf einer Adresse muss ihre Kategorie offen sein, auch wenn der
 * gemerkte Zustand sie zu hat — sonst führte ein Lesezeichen auf eine Seite,
 * deren Menüpunkt nicht zu sehen ist. Danach gehört der Zustand dem Anwender:
 * Ein Effekt, der die Kategorie der laufenden Seite bei jeder Änderung wieder
 * öffnete, machte sie unschliessbar.
 */
export function useSeitenspalte(
  alleSchluessel: readonly string[],
  aktiveKategorie: string | undefined,
): Seitenspalte {
  const [eingeklappt, setEingeklappt] = useState<boolean>(gemerktEingeklappt);
  const [offen, setOffen] = useState<ReadonlySet<string>>(() => {
    const gemerkt = new Set(gemerkteOffeneGruppen(alleSchluessel));
    if (aktiveKategorie !== undefined) gemerkt.add(aktiveKategorie);
    return gemerkt;
  });

  const einklappenUmschalten = useCallback((): void => {
    const nachher = !eingeklappt;
    setEingeklappt(nachher);
    merken(EINGEKLAPPT_SCHLUESSEL, nachher ? 'ja' : 'nein');
  }, [eingeklappt]);

  const gruppeUmschalten = useCallback(
    (key: string): void => {
      // **Der neue Zustand wird HIER gerechnet und nicht in einem
      // Zustandsaktualisierer.** React ruft einen Aktualisierer im strengen
      // Modus zweimal auf; ein `localStorage.setItem` darin liefe doppelt, und
      // ein Wert, den man aus einem Aktualisierer heraus in eine Variable
      // schreibt, ist beim nächsten Aufruf noch gar nicht gesetzt.
      if (eingeklappt) {
        setEingeklappt(false);
        merken(EINGEKLAPPT_SCHLUESSEL, 'nein');
      }
      const nachher = new Set(offen);
      // Aus der schmalen Spalte heraus wird nur GEÖFFNET: Ein Klick auf ein
      // Sinnbild in einer Spalte ohne Beschriftungen ist der Wunsch, etwas zu
      // sehen, und nicht der, es wegzuklappen.
      if (eingeklappt || !nachher.has(key)) nachher.add(key);
      else nachher.delete(key);
      setOffen(nachher);
      merken(OFFEN_SCHLUESSEL, JSON.stringify([...nachher]));
    },
    [eingeklappt, offen],
  );

  return { eingeklappt, einklappenUmschalten, offen, gruppeUmschalten };
}
